import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  Renderer2,
  OnInit,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';

import {
  BusinessDocument,
  BusinessDocumentPipelineBundle,
  BusinessDocumentStepAccess,
  BusinessDocumentWorkspaceModel,
} from '../../../core/services/business-document.service';
import {
  BusinessDocumentPipelineOption,
} from '../business-document-pipeline-picker/business-document-pipeline-picker';
import { pipelinePath } from '../pipeline-path';
import { BusinessDocumentEventRecord } from '../business-document-events/business-document-event-timeline';

type BusinessDocumentDrawer = 'reviews' | 'history' | 'view' | 'print' | 'email' | 'chat';
type BusinessDocumentLifecycle = 'new' | 'draft' | 'in-step' | 'out-step' | 'in-review' | 'failed' | 'successful';

@Component({
  selector: 'app-business-document-wrapper',
  standalone: false,
  templateUrl: './business-document-wrapper.html',
  styleUrl: './business-document-wrapper.scss',
})
export class BusinessDocumentWrapper implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() businessDocumentId = '';
  @Input() businessDocumentType = '';
  @Input() branchId = '';
  @Input() pipelineId = '';
  @Input() pipelineOptions: BusinessDocumentPipelineOption[] = [];
  @Input() pipelineDisabled = false;
  @Input() restrictPipelineOptions = false;
  @Input() completedInventoryStepIds?: string[];
  @Input() pipelineError = '';
  @Input() pipelineFree = false;
  @Input() compact = false;
  @Input() viewRoute = '';
  @Input() historyDocumentOwner: BusinessDocumentEventRecord['document_owner'] = '';
  @Input() historyDocumentType = '';
  @Input() historyDocumentId = '';
  @Input() history: unknown[] | null = [];
  @Input() historyRefreshKey = 0;
  // Lifecycle loading may notify a parent whose template was already checked.
  // Deliver outputs after the current pass instead of changing it mid-check.
  @Output() accessModeChange = new EventEmitter<'observe' | 'edit'>(true);
  @Output() pipelineIdChange = new EventEmitter<string>(true);
  @Output() workspaceChange = new EventEmitter<BusinessDocumentWorkspaceModel | null>(true);
  @Output() previewPipelineChange = new EventEmitter<BusinessDocumentPipelineBundle | null>(true);

  @ViewChild('contentHost', { read: ElementRef }) contentHost?: ElementRef<HTMLElement>;
  @ViewChild('drawerPanel', { read: ElementRef }) drawerPanel?: ElementRef<HTMLElement>;

  workspace: BusinessDocumentWorkspaceModel | null = null;
  access: BusinessDocumentStepAccess | null = null;
  previewPipeline: BusinessDocumentPipelineBundle | null = null;
  availablePipelines: BusinessDocumentPipelineBundle[] = [];
  loading = false;
  errorMessage = '';
  selectedContributorRole = '';
  activeDrawer: BusinessDocumentDrawer | null = null;
  accessMode: 'observe' | 'edit' = 'observe';

  private readonly originalDisabledState = new Map<HTMLElement, boolean>();
  private previewRequest = 0;
  private trackerStageSource: BusinessDocumentPipelineBundle | null = null;
  private trackerStageCurrentId: string | null = null;
  private trackerHistoryKey = '';
  private trackerStageCache: Array<{
    key: string;
    label: string;
    color: string | null;
    verificationRequired: boolean;
    stepType: string;
  }> = [];
  private pickerAvailableSource: BusinessDocumentPipelineBundle[] | null = null;
  private pickerInputSource: BusinessDocumentPipelineOption[] | null = null;
  private pickerOptionCache: BusinessDocumentPipelineOption[] = [];
  private drawerTrigger: HTMLElement | null = null;
  private previousBodyOverflow: string | null = null;
  private contentObserver: MutationObserver | null = null;

  constructor(
    private readonly documents: BusinessDocument,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.renderer.addClass(document.body, 'business-document-wrapper-active');
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(document.body, 'business-document-wrapper-active');
    this.contentObserver?.disconnect();
    this.syncReadOnlyControls(false);
    document.body.classList.remove('business-document-drawer-open');
    this.restoreBodyOverflow();
  }

  get readOnly(): boolean {
    return Boolean(!this.pipelineFree && this.businessDocumentId && this.accessMode === 'observe');
  }

  get inReview(): boolean {
    return Boolean(
      this.workspace?.verification_requests.some(
        (request) => String(request['status'] ?? '').toLowerCase() === 'pending',
      ),
    );
  }

  get currentStepRequiresVerification(): boolean {
    const currentStepId = this.workspace?.document.current_step_id;
    if (!currentStepId) return false;
    const currentStep = this.workspace?.pipeline.steps.find(
      (item) => item.step.id === currentStepId,
    );
    return Boolean(
      currentStep?.step.verification_enabled
      || currentStep?.verification_groups.some((group) => group.group.active),
    );
  }

  get pendingReviewCount(): number {
    return this.workspace?.verification_requests.filter(
      (request) => String(request['status'] ?? '').toLowerCase() === 'pending',
    ).length ?? 0;
  }

  get activityDocumentOwner(): BusinessDocumentEventRecord['document_owner'] {
    return this.historyDocumentOwner.trim()
      || this.workspace?.document.owner_type?.trim()
      || '';
  }

  get activityDocumentType(): string {
    return this.historyDocumentType.trim()
      || this.workspace?.document.document_kind?.trim()
      || '';
  }

  get activityDocumentId(): string {
    return this.historyDocumentId.trim()
      || this.workspace?.document.owner_record_id?.trim()
      || '';
  }

  get inOutStep(): boolean {
    return Boolean(this.workspace?.pipeline.steps.some(
      (item) => item.step.id === this.workspace?.document.current_step_id && item.step.is_out_step,
    ));
  }

  get lifecycleState(): BusinessDocumentLifecycle {
    if (!this.businessDocumentId.trim()) return 'new';
    const status = String(this.workspace?.document.status ?? '').trim().toLowerCase();
    if (this.inReview || status === 'in_review') return 'in-review';
    if (status === 'out_step') return 'out-step';
    if (status === 'draft') return 'draft';
    if (['failed', 'rejected', 'cancelled', 'canceled', 'error'].includes(status)) return 'failed';
    const currentStep = this.workspace?.pipeline.steps.find(
      (item) => item.step.id === this.workspace?.document.current_step_id,
    )?.step;
    if (
      currentStep?.is_terminal
      || ['successful', 'success', 'finished', 'completed', 'closed', 'done'].includes(status)
    ) {
      return 'successful';
    }
    return 'in-step';
  }

  get lifecycleLabel(): string {
    return {
      new: 'New',
      draft: 'Draft',
      'in-step': 'In step',
      'out-step': 'Awaiting participant',
      'in-review': 'In review',
      failed: 'Failed',
      successful: 'Successful',
    }[this.lifecycleState];
  }

  get lifecycleBadgeClass(): string {
    return {
      new: 'bg-azure-lt text-azure',
      draft: 'bg-secondary-lt text-secondary',
      'in-step': 'bg-blue-lt text-blue',
      'out-step': 'bg-purple-lt text-purple',
      'in-review': 'bg-yellow-lt text-yellow',
      failed: 'bg-red-lt text-red',
      successful: 'bg-green-lt text-green',
    }[this.lifecycleState];
  }

  get trackerStages(): Array<{
    key: string;
    label: string;
    color: string | null;
    verificationRequired: boolean;
    stepType: string;
  }> {
    const source = this.workspace?.pipeline ?? this.previewPipeline;
    const isDraft = String(this.workspace?.document.status ?? '').toLowerCase() === 'draft';
    const currentId = isDraft
      ? null
      : this.workspace?.document.current_step_id
        ?? null;
    const historyKey = this.completedInventoryStepIds === undefined ? 'untracked' : JSON.stringify(this.completedInventoryStepIds);
    if (source === this.trackerStageSource && currentId === this.trackerStageCurrentId && historyKey === this.trackerHistoryKey) {
      return this.trackerStageCache;
    }

    this.trackerHistoryKey = historyKey;
    this.trackerStageSource = source ?? null;
    this.trackerStageCurrentId = currentId;
    const steps = (source?.steps ?? [])
      .filter((item) => item.step.active || this.completedInventoryStepIds?.includes(item.step.id))
      .sort((left, right) => left.step.sequence - right.step.sequence);
    if (!source || !steps.length) {
      this.trackerStageCache = [];
      return this.trackerStageCache;
    }

    const visibleIds = pipelinePath(source, currentId, this.completedInventoryStepIds);
    const stepById = new Map(steps.map((item) => [item.step.id, item]));
    this.trackerStageCache = visibleIds.flatMap((id) => {
      const bundle = stepById.get(id);
      if (!bundle) return [];
      return [{
        key: bundle.step.id,
        label: bundle.step.name,
        color: bundle.step.color,
        stepType: bundle.step.step_type,
        verificationRequired: bundle.step.verification_enabled
          || bundle.verification_groups.some((group) => group.group.active),
      }];
    });
    return this.trackerStageCache;
  }

  get trackerCurrent(): string | null {
    if (this.workspace) {
      return String(this.workspace.document.status).toLowerCase() === 'draft'
        ? null
        : this.workspace.document.current_step_id;
    }
    // A selected pipeline is only a preview until the document is published.
    // Keeping current unset makes every stage white for new and draft records.
    return null;
  }

  get pickerOptions(): BusinessDocumentPipelineOption[] {
    if (this.restrictPipelineOptions) {
      const validIds = new Set(this.availablePipelines.map(item => item.pipeline.id));
      return this.pipelineOptions.filter(option => validIds.has(option.value));
    }
    if (
      this.availablePipelines === this.pickerAvailableSource
      && this.pipelineOptions === this.pickerInputSource
    ) {
      return this.pickerOptionCache;
    }

    this.pickerAvailableSource = this.availablePipelines;
    this.pickerInputSource = this.pipelineOptions;
    if (this.availablePipelines.length) {
      this.pickerOptionCache = this.availablePipelines.map((item) => ({
        value: item.pipeline.id,
        label: `${item.pipeline.name}${item.pipeline.is_default ? ' (Default)' : ''}`,
      }));
      return this.pickerOptionCache;
    }

    this.pickerOptionCache = this.pipelineOptions;
    return this.pickerOptionCache;
  }

  get contributorRoles(): string[] {
    return this.workspace?.available_contributor_roles ?? [];
  }

  get canUseEditMode(): boolean {
    return Boolean(this.access?.allowed_view_types.includes('edit'));
  }

  get showStepView(): boolean {
    return Boolean(
      this.access?.allowed_view_types.includes('edit')
      && this.access.allowed_view_types.includes('observe'),
    );
  }

  get editingContributorLabel(): string {
    return this.access?.editing_contributor?.role_name ?? 'Editor';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessDocumentId']?.previousValue !== changes['businessDocumentId']?.currentValue) {
      this.accessMode = 'observe';
    }
    if (this.pipelineFree) {
      this.resetWorkflow();
      this.scheduleReadOnlySync();
      return;
    }

    if (this.businessDocumentId.trim() && (
      changes['businessDocumentId']
      || changes['businessDocumentType']
      || changes['branchId']
      || changes['pipelineId']
      || changes['pipelineFree']
    )) {
      // A saved document owns its pipeline. Hydrating branch/pipeline inputs
      // must reload that workspace, not clear it to load a creation preview.
      this.previewRequest += 1;
      void this.load();
      this.scheduleReadOnlySync();
      return;
    }

    if (
      changes['businessDocumentId']
      || changes['businessDocumentType']
      || changes['branchId']
      || changes['pipelineId']
      || changes['pipelineFree']
      || changes['restrictPipelineOptions']
      || (this.restrictPipelineOptions && changes['pipelineOptions'])
    ) {
      this.workspace = null;
      this.access = null;
      void this.loadPreview();
    }
    this.scheduleReadOnlySync();
  }

  ngAfterViewInit(): void {
    const host = this.contentHost?.nativeElement;
    if (host) {
      this.contentObserver = new MutationObserver(() => this.scheduleReadOnlySync());
      this.contentObserver.observe(host, { childList: true, subtree: true });
    }
    this.scheduleReadOnlySync();
  }

  async load(): Promise<void> {
    if (this.pipelineFree) {
      this.resetWorkflow();
      return;
    }

    const id = this.businessDocumentId.trim();
    if (!id) {
      this.workspace = null;
      await this.loadPreview();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    try {
      const [workspace, access] = await Promise.all([
        firstValueFrom(this.documents.workspace(id)),
        firstValueFrom(this.documents.access(id)),
      ]);
      this.workspace = workspace;
      this.access = access;
      const editAllowed = access.allowed_view_types.includes('edit');
      const observeAllowed = access.allowed_view_types.includes('observe');
      this.accessMode = editAllowed && (this.accessMode === 'edit' || !observeAllowed)
        ? 'edit' : 'observe';
      this.accessModeChange.emit(this.accessMode);
      this.workspaceChange.emit(this.workspace);
      this.scheduleReadOnlySync();
      const roles = this.contributorRoles;
      if (!roles.includes(this.selectedContributorRole)) {
        this.selectedContributorRole = roles[0] ?? '';
      }
    } catch (error: any) {
      this.workspace = null;
      this.access = null;
      this.workspaceChange.emit(null);
      this.errorMessage = error?.error?.message
        ?? 'The business-document workflow could not be loaded.';
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  refresh(): Promise<void> {
    return this.load();
  }

  choosePipeline(pipelineId: string): void {
    if (this.restrictPipelineOptions && !this.pickerOptions.some(option => option.value === pipelineId)) return;
    this.pipelineId = pipelineId;
    this.previewPipeline = this.availablePipelines.find(
      (item) => item.pipeline.id === pipelineId,
    ) ?? null;
    this.previewPipelineChange.emit(this.previewPipeline);
    this.pipelineIdChange.emit(pipelineId);
    if (!this.previewPipeline && pipelineId) void this.loadPipeline(pipelineId);
  }

  setAccessMode(mode: 'observe' | 'edit'): void {
    if (mode === 'edit' && !this.canUseEditMode) return;
    this.accessMode = mode;
    this.accessModeChange.emit(mode);
    this.scheduleReadOnlySync();
  }

  contributorRoleLabel(code: string): string {
    return this.workspace?.pipeline.contributors
      .find((contributor) => contributor.contributor_role_code === code)
      ?.contributor_role_name ?? code;
  }

  print(): void {
    this.openDrawer('print');
  }

  view(): void {
    this.openDrawer('view');
  }

  email(): void {
    this.openDrawer('email');
  }

  chat(): void {
    this.openDrawer('chat');
  }

  async openDrawer(drawer: BusinessDocumentDrawer, trigger?: EventTarget | null): Promise<void> {
    if (!this.businessDocumentId) return;
    if (!this.workspace) await this.load();
    if (!this.workspace) return;
    const activeElement = document.activeElement;
    this.drawerTrigger = trigger instanceof HTMLElement
      ? trigger
      : activeElement instanceof HTMLElement ? activeElement : null;
    this.activeDrawer = drawer;
    document.body.classList.add('business-document-drawer-open');
    if (this.previousBodyOverflow === null) {
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    setTimeout(() => {
      this.drawerPanel?.nativeElement
        .querySelector<HTMLElement>('[data-drawer-initial-focus]')
        ?.focus();
    });
  }

  closeDrawer(): void {
    if (!this.activeDrawer) return;
    this.activeDrawer = null;
    document.body.classList.remove('business-document-drawer-open');
    this.restoreBodyOverflow();
    const trigger = this.drawerTrigger;
    this.drawerTrigger = null;
    setTimeout(() => trigger?.focus());
  }

  printablePayload() {
    const workspace = this.workspace!;
    const snapshot = this.latestSnapshot();
    const rawLines = Array.isArray(snapshot['lines']) ? snapshot['lines'] : [];
    return {
      documentType: workspace.document.document_type_key,
      documentNumber: workspace.document.document_number,
      title: workspace.document.display_name,
      fields: [
        { label: 'Status', value: workspace.document.status },
        { label: 'Stage', value: workspace.document.stage },
      ],
      table: rawLines.length ? {
        headers: ['Description', 'Quantity', 'Unit price', 'Total'],
        rows: rawLines.filter((line): line is Record<string, unknown> => this.isRecord(line)).map((line) => [
          this.text(line['description'] ?? line['product_name'] ?? line['name']) || 'Line',
          String(this.number(line['quantity'])),
          String(this.number(line['unit_price'] ?? line['price'])),
          String(this.number(line['subtotal'] ?? line['amount_total'])),
        ]),
      } : undefined,
      totals: [{ label: 'Total', value: `${workspace.document.amount_total} ${workspace.document.currency_id || ''}` }],
      fileName: `${workspace.document.document_number}.pdf`,
    };
  }

  drawerTitle(): string {
    switch (this.activeDrawer) {
      case 'reviews': return 'Reviews';
      case 'history': return 'History';
      case 'view': return 'View document';
      case 'print': return 'Print document';
      case 'email': return 'Send by email';
      case 'chat': return 'Document chat';
      default: return 'Document tools';
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleDrawerKeydown(event: KeyboardEvent): void {
    if (!this.activeDrawer) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = this.drawerPanel?.nativeElement;
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )).filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  isActionVisible(...actionCodes: string[]): boolean {
    if (!this.businessDocumentId || !this.workspace?.document.current_step_id) {
      return true;
    }

    const metadata = this.workspace.pipeline.steps
      .find((item) => item.step.id === this.workspace?.document.current_step_id)
      ?.step.metadata;
    const configured = metadata?.['action_buttons'];
    if (!Array.isArray(configured) || (configured.length === 0 && metadata?.['action_buttons_explicit'] !== true)) {
      return true;
    }

    const expected = new Set(actionCodes.map((code) => code.trim().toLowerCase()));
    return configured.some((button) => {
      if (!button || typeof button !== 'object') return false;
      const value = button as { code?: unknown; active?: unknown };
      return value.active !== false
        && typeof value.code === 'string'
        && expected.has(value.code.trim().toLowerCase());
    });
  }

  private async loadPreview(): Promise<void> {
    const typeKey = this.businessDocumentType.trim();
    if (this.pipelineFree || this.businessDocumentId.trim() || !typeKey) {
      this.previewPipeline = null;
      this.availablePipelines = [];
      this.previewPipelineChange.emit(null);
      this.cdr.markForCheck();
      return;
    }

    const request = ++this.previewRequest;
    this.loading = true;
    this.errorMessage = '';
    try {
      const pipelines = await firstValueFrom(
        this.documents.pipelines(typeKey, this.branchId.trim() || undefined),
      );
      if (request !== this.previewRequest) return;
      this.availablePipelines = pipelines.filter((pipeline) => pipeline.is_valid);
      const selectable = this.restrictPipelineOptions
        ? this.availablePipelines.filter(item => this.pipelineOptions.some(option => option.value === item.pipeline.id))
        : this.availablePipelines;
      const selected = selectable.find((item) => item.pipeline.id === this.pipelineId)
        ?? selectable.find((item) => item.pipeline.is_default)
        ?? selectable[0]
        ?? null;
      this.previewPipeline = selected;
      this.previewPipelineChange.emit(selected);
      if (selected && selected.pipeline.id !== this.pipelineId) {
        this.pipelineId = selected.pipeline.id;
        this.pipelineIdChange.emit(this.pipelineId);
      }
      if (!selected) {
        if (this.restrictPipelineOptions && this.pipelineId) {
          this.pipelineId = '';
          this.pipelineIdChange.emit('');
        }
        this.errorMessage = this.restrictPipelineOptions ? 'No compatible pipeline is available for the selected location. Check its Stock Transfer settings.' : 'No valid pipeline is available. Fix or create a pipeline before creating this document.';
      }
    } catch {
      if (request === this.previewRequest) {
        this.availablePipelines = [];
        this.previewPipeline = null;
        this.previewPipelineChange.emit(null);
        this.errorMessage = 'Available pipelines could not be loaded.';
      }
    } finally {
      if (request === this.previewRequest) {
        this.loading = false;
        this.cdr.markForCheck();
      }
    }
  }

  private async loadPipeline(pipelineId: string): Promise<void> {
    try {
      const pipeline = await firstValueFrom(this.documents.pipeline(pipelineId));
      if (this.pipelineId === pipelineId) {
        this.previewPipeline = pipeline;
        this.previewPipelineChange.emit(pipeline);
        this.cdr.markForCheck();
      }
    } catch {
      this.errorMessage = 'The selected pipeline preview could not be loaded.';
      this.cdr.markForCheck();
    }
  }

  private resetWorkflow(): void {
    this.previewRequest += 1;
    this.workspace = null;
    this.access = null;
    this.previewPipeline = null;
    this.previewPipelineChange.emit(null);
    this.availablePipelines = [];
    this.loading = false;
    this.errorMessage = '';
    this.accessMode = 'observe';
    this.closeDrawer();
    this.workspaceChange.emit(null);
  }

  private latestSnapshot(): Record<string, unknown> {
    const timeline = this.workspace?.timeline ?? [];
    const latest = timeline.length ? timeline[timeline.length - 1] : null;
    const record = latest?.['record'];
    return this.isRecord(record) ? record : {};
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private text(value: unknown): string {
    return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  }

  private number(value: unknown): number {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private restoreBodyOverflow(): void {
    if (this.previousBodyOverflow === null) return;
    document.body.style.overflow = this.previousBodyOverflow;
    this.previousBodyOverflow = null;
  }

  private scheduleReadOnlySync(): void {
    queueMicrotask(() => this.syncReadOnlyControls());
  }

  private syncReadOnlyControls(forceDisable?: boolean): void {
    const host = this.contentHost?.nativeElement;
    if (!host) return;
    const shouldDisable = forceDisable ?? (this.readOnly || this.inReview);
    const controls = host.querySelectorAll<HTMLElement>(
      'input, select, textarea, button:not(.nav-link):not([role="tab"])',
    );

    if (shouldDisable) {
      controls.forEach((control) => {
        // Independently authorized related records do not mutate the owner document.
        if (control.closest('[data-document-independent]')) return;
        if (!this.originalDisabledState.has(control)) {
          this.originalDisabledState.set(
            control,
            (control as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement).disabled,
          );
        }
        (control as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement).disabled = true;
        control.setAttribute('aria-disabled', 'true');
      });
      return;
    }

    host.querySelectorAll('.card[disabled]').forEach(card => card.removeAttribute('disabled'));
    this.originalDisabledState.forEach((wasDisabled, control) => {
      if (control.isConnected) {
        (control as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement).disabled = wasDisabled;
        control.removeAttribute('aria-disabled');
      }
    });
    this.originalDisabledState.clear();
  }
}
